import { queryOptions } from "@tanstack/react-query";
import { axiosInstance } from "./axios";
import { z } from "zod";

const playerStatsSchema = z.object({
    playerId: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    line: z.number(),
    role: z.string(),
    timeOnIce: z.number().nullable(),
    pointsDefensive: z.number(),
    pointsOffensive: z.number(),
    pointsTeamPlay: z.number(),
    pointsEffort: z.number(),
    pointsBonus: z.number(),
    totalPoints: z.number(),
    matchesPlayed: z.number(),
});

const matchInfoSchema = z.object({
    matchId: z.number(),
    date: z.string(),
    tournamentName: z.string(),
    awayTeam: z.string(),
    awayGoals: z.number(),
    homeTeam: z.string(),
    homeGoals: z.number(),
    homeTeamId: z.number().optional(),
    awayTeamId: z.number().optional(),
});

const shotSchema = z
    .object({
        id: z.number(),
        team: z.string().optional(),
        period: z.number(),
        secondsFromPeriodStart: z.number().optional(),
        startXPosition: z.number(),
        startYPosition: z.number(),
        speed: z.number().optional(),
        result: z.string(),
        shooterId: z.number().optional(),
        expectedGoals: z.number().nullable(),
        fromLateralPass: z.boolean().optional(),
        royalRoadCrossed: z.boolean().optional(),
    })
    .passthrough();

const matchKpisSchema = z.object({
    average: z.array(playerStatsSchema),
    total: z.array(playerStatsSchema),
    matches: z.array(matchInfoSchema),
});

const goalkeeperStatsSchema = z.object({
    playerId: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    role: z.string(),
    pointsGSAx: z.number(),
    pointsHighDangerShots: z.number(),
    pointsLowDangerShots: z.number(),
    pointsLateralPassShots: z.number(),
    pointsScreenedShots: z.number(),
    totalPoints: z.number(),
    matchesPlayed: z.number(),
});

const goalkeeperKpisSchema = z.object({
    average: z.array(goalkeeperStatsSchema),
    total: z.array(goalkeeperStatsSchema),
    matches: z.array(matchInfoSchema),
});

const matchShotsResponseSchema = z.array(
    z.object({
        matchId: z.number(),
        shots: z.array(shotSchema),
    })
);

const playerMatchKpiSchema = z.object({
    matchId: z.number(),
    role: z.string(),
    pointsDefensive: z.number(),
    pointsOffensive: z.number(),
    pointsTeamPlay: z.number(),
    pointsEffort: z.number(),
    pointsBonus: z.number(),
    totalPoints: z.number(),
});

const goalkeeperMatchKpiSchema = z.object({
    matchId: z.number(),
    role: z.string(),
    pointsGSAx: z.number(),
    pointsHighDangerShots: z.number(),
    pointsLowDangerShots: z.number(),
    pointsLateralPassShots: z.number(),
    pointsScreenedShots: z.number(),
    totalPoints: z.number(),
});

export const goalkeeperMatchStatsSchema = z.object({
    playerId: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    role: z.string(),
    pointsGSAx: z.number(),
    pointsHighDangerShots: z.number(),
    pointsLowDangerShots: z.number(),
    pointsLateralPassShots: z.number(),
    pointsScreenedShots: z.number(),
    totalPoints: z.number(),
    matchesPlayed: z.number(),
});

export type Shot = z.infer<typeof shotSchema> & {
    matchId?: number;
};

export type PlayerStats = z.infer<typeof playerStatsSchema>;
export type GoalkeeperStats = z.infer<typeof goalkeeperStatsSchema>;
export type MatchKpis = z.infer<typeof matchKpisSchema>;
export type GoalkeeperKpis = z.infer<typeof goalkeeperKpisSchema>;
export type MatchInfo = z.infer<typeof matchInfoSchema>;
export type PlayerMatchKpi = z.infer<typeof playerMatchKpiSchema>;
export type GoalkeeperMatchKpi = z.infer<typeof goalkeeperMatchKpiSchema>;
export type GoalkeeperMatchStats = z.infer<typeof goalkeeperMatchStatsSchema>;

function normalizeMatchIds(matchIds?: number | number[]): number[] {
    if (typeof matchIds === "number") {
        return [matchIds];
    }
    if (Array.isArray(matchIds)) {
        return matchIds;
    }
    return [];
}

export const fetchMatchKpis = async (matchIds?: number | number[], teamId?: number): Promise<MatchKpis> => {
    const matchIdsArray = normalizeMatchIds(matchIds);

    try {
        const response = await axiosInstance.post("/matches/kpis", { matchIds: matchIdsArray, teamId });
        return matchKpisSchema.parse(response.data);
    } catch (error) {
        const idsText = matchIdsArray.length > 0 ? ` for IDs ${matchIdsArray.join(", ")}` : "";
        throw new Error(`Failed to fetch match KPIs${idsText}: ${error}`);
    }
};

export const matchKpisQueryOptions = (matchIds?: number | number[], teamId?: number) => {
    const normalizedIds = normalizeMatchIds(matchIds);
    return queryOptions({
        queryKey: ["match-kpis", normalizedIds, teamId],
        queryFn: () => fetchMatchKpis(normalizedIds, teamId),
        staleTime: 5 * 60 * 1000,
    });
};

export const fetchGoalkeeperKpis = async (matchIds?: number | number[], teamId?: number): Promise<GoalkeeperKpis> => {
    const matchIdsArray = normalizeMatchIds(matchIds);

    try {
        const response = await axiosInstance.post("/matches/goalkeepers/kpis", { matchIds: matchIdsArray, teamId });
        return goalkeeperKpisSchema.parse(response.data);
    } catch (error) {
        const idsText = matchIdsArray.length > 0 ? ` for IDs ${matchIdsArray.join(", ")}` : "";
        throw new Error(`Failed to fetch goalkeeper KPIs${idsText}: ${error}`);
    }
};

export const goalkeeperKpisQueryOptions = (matchIds?: number | number[], teamId?: number) => {
    const normalizedIds = normalizeMatchIds(matchIds);
    return queryOptions({
        queryKey: ["goalkeeper-kpis", normalizedIds, teamId],
        queryFn: () => fetchGoalkeeperKpis(normalizedIds, teamId),
        staleTime: 5 * 60 * 1000,
    });
};

export const fetchMatchShots = async (matchIds?: number | number[], teamId?: number): Promise<Shot[]> => {
    const matchIdsArray = normalizeMatchIds(matchIds);

    try {
        const response = await axiosInstance.post("/matches/shots", { matchIds: matchIdsArray, teamId });
        const parsedResponse = matchShotsResponseSchema.parse(response.data);

        const allShots: Shot[] = [];
        parsedResponse.forEach((match) => {
            match.shots.forEach((shot) => {
                allShots.push({
                    ...shot,
                    matchId: match.matchId,
                });
            });
        });

        return allShots;
    } catch (error) {
        const idsText = matchIdsArray.length > 0 ? ` for IDs ${matchIdsArray.join(", ")}` : "";
        throw new Error(`Failed to fetch match shots${idsText}: ${error}`);
    }
};

export const matchShotsQueryOptions = (matchIds?: number | number[], teamId?: number) => {
    const normalizedIds = normalizeMatchIds(matchIds);
    return queryOptions({
        queryKey: ["match-shots", normalizedIds, teamId],
        queryFn: () => fetchMatchShots(normalizedIds, teamId),
        staleTime: 5 * 60 * 1000,
    });
};

export const fetchPlayerMatchKpis = async (
    playerId: number,
    matchIds?: number | number[],
    teamId?: number
): Promise<PlayerMatchKpi[]> => {
    const matchIdsArray = normalizeMatchIds(matchIds);

    try {
        const response = await axiosInstance.post(`/matches/kpis/${playerId}`, { matchIds: matchIdsArray, teamId });
        return z.array(playerMatchKpiSchema).parse(response.data);
    } catch (error) {
        const idsText = matchIdsArray.length > 0 ? ` for matches ${matchIdsArray.join(", ")}` : "";
        throw new Error(`Failed to fetch player ${playerId} match KPIs${idsText}: ${error}`);
    }
};

export const playerMatchKpisQueryOptions = (playerId: number, matchIds?: number | number[], teamId?: number) => {
    const normalizedIds = normalizeMatchIds(matchIds);
    return queryOptions({
        queryKey: ["player-match-kpis", playerId, normalizedIds, teamId],
        queryFn: () => fetchPlayerMatchKpis(playerId, normalizedIds, teamId),
        enabled: !!playerId,
        staleTime: 5 * 60 * 1000,
    });
};

export const fetchGoalkeeperMatchStats = async (
    playerId: number,
    matchIds?: number | number[],
    teamId?: number
): Promise<GoalkeeperMatchKpi[]> => {
    const matchIdsArray = normalizeMatchIds(matchIds);

    try {
        const response = await axiosInstance.post(`/matches/goalkeepers/kpis/${playerId}`, {
            matchIds: matchIdsArray,
            teamId,
        });
        return z.array(goalkeeperMatchKpiSchema).parse(response.data);
    } catch (error) {
        const idsText = matchIdsArray.length > 0 ? ` for matches ${matchIdsArray.join(", ")}` : "";
        throw new Error(`Failed to fetch goalkeeper ${playerId} match stats${idsText}: ${error}`);
    }
};

export const goalkeeperMatchStatsQueryOptions = (playerId: number, matchIds?: number | number[], teamId?: number) => {
    const normalizedIds = normalizeMatchIds(matchIds);
    return queryOptions({
        queryKey: ["goalkeeper-match-stats", playerId, normalizedIds, teamId],
        queryFn: () => fetchGoalkeeperMatchStats(playerId, normalizedIds, teamId),
        enabled: !!playerId,
        staleTime: 5 * 60 * 1000,
    });
};

const periodKpiDataSchema = z.object({
    periodId: z.number(),
    timeOnIce: z.number().nullable(),
    pointsDefensive: z.number().nullable(),
    pointsOffensive: z.number().nullable(),
    pointsTeamPlay: z.number().nullable(),
    pointsEffort: z.number().nullable(),
    pointsBonus: z.number().nullable(),
    totalPoints: z.number().nullable(),
});

const periodPlayerDataSchema = z.object({
    playerId: z.number(),
    playerName: z.string().nullable(),
    playerLastName: z.string().nullable(),
    teamId: z.number(),
    teamName: z.string().nullable(),
    role: z.string().nullable(),
    period1: periodKpiDataSchema,
    period2: periodKpiDataSchema,
    period3: periodKpiDataSchema,
});

const periodStatsResponseSchema = z.object({
    periodKpiData: z.array(periodPlayerDataSchema),
    opponentPeriodKpiData: z.array(periodPlayerDataSchema),
});

export type PeriodKpiData = z.infer<typeof periodKpiDataSchema>;
export type PeriodPlayerData = z.infer<typeof periodPlayerDataSchema>;
export type PeriodStatsResponse = z.infer<typeof periodStatsResponseSchema>;

export const periodPlayersDataQueryOptions = (matchIds?: number | number[], teamId?: number) => {
    const normalizedIds = normalizeMatchIds(matchIds);
    return queryOptions({
        queryKey: ["period-players-data", normalizedIds, teamId],
        queryFn: async () => {
            const response = await axiosInstance.post("/matches/analytics/period", {
                matchIds: normalizedIds,
                teamId,
            });
            return periodStatsResponseSchema.parse(response.data);
        },
        staleTime: 5 * 60 * 1000,
    });
};

const leaderboardPlayerSchema = z.object({
    playerId: z.number(),
    teamId: z.number(),
    playerName: z.string().nullable(),
    playerLastName: z.string().nullable(),
    role: z.array(z.string()),
    period1: z.number().nullable(),
    period2: z.number().nullable(),
    period3: z.number().nullable(),
    totalPoints: z.number().nullable(),
});

const matchLeaderboardResponseSchema = z.object({
    skaterStats: z.array(leaderboardPlayerSchema),
    goalieStats: z.array(leaderboardPlayerSchema),
});

export type LeaderboardPlayer = z.infer<typeof leaderboardPlayerSchema>;
export type MatchLeaderboardResponse = z.infer<typeof matchLeaderboardResponseSchema>;

export const matchLeaderboardQueryOptions = (matchIds?: number | number[], teamId?: number) => {
    const normalizedIds = normalizeMatchIds(matchIds);
    return queryOptions({
        queryKey: ["match-leaderboard", normalizedIds, teamId],
        queryFn: async () => {
            const response = await axiosInstance.post("/matches/matches-leaderboard", {
                matchIds: normalizedIds,
                teamId,
            });
            return matchLeaderboardResponseSchema.parse(response.data);
        },
        staleTime: 5 * 60 * 1000,
    });
};
